import React, { useState, useEffect } from 'react';
import './Home.css';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaRocket, FaShieldAlt, FaTruck, FaHeadset, FaBolt, FaShoppingCart, FaHeadphones, FaVideo, FaClock, FaCar, FaArrowRight, FaTv, FaLightbulb, FaGift, FaDesktop } from 'react-icons/fa';
import { db } from '../../firebaseConfig';
import { getImageVariants } from "../../utils/imageVariants";
import { collection, query, where, limit, getDocs, doc, getDoc } from 'firebase/firestore';

const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect width='100%25' height='100%25' fill='%231a1a25'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2371717a' font-family='Arial' font-size='18'%3ESin%20imagen%3C/text%3E%3C/svg%3E";

// Si falla la miniatura se prueba la imagen original y, recién después, el placeholder.
const imgFallback = (e, original) => {
  const img = e.currentTarget;
  img.removeAttribute("srcset");
  if (original && img.dataset.fallback !== "original") {
    img.dataset.fallback = "original";
    img.src = original;
  } else {
    img.src = FALLBACK_IMAGE;
  }
};

const Home = ({ greeting }) => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [featuredSource, setFeaturedSource] = useState("random"); // "ml-top" | "random"
  const [kits, setKits] = useState([]);
  const [loadingKits, setLoadingKits] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      // 1) Intentar primero el cache de "top vendidos en MercadoLibre"
      //    (lo actualiza el AdminDashboard cada vez que se cargan ventas).
      try {
        const topRef = doc(db, "featuredProducts", "topML");
        const topSnap = await getDoc(topRef);
        if (topSnap.exists()) {
          const data = topSnap.data();
          const items = Array.isArray(data.items) ? data.items : [];
          // Hidratamos cada item con datos frescos del producto (stock/precio)
          const fresh = await Promise.all(
            items.slice(0, 4).map(async (it) => {
              try {
                const pSnap = await getDoc(doc(db, "productos", it.productId));
                if (!pSnap.exists()) return null;
                const p = { id: pSnap.id, ...pSnap.data() };
                if (p.hidden === true) return null;
                if (Number(p.stock || 0) <= 0) return null;
                if (!p.image || !String(p.image).startsWith("http")) return null;
                return p;
              } catch (_) {
                return null;
              }
            })
          );
          const valid = fresh.filter(Boolean);
          console.log(`[Home] featuredProducts/topML: ${items.length} guardados, ${valid.length} válidos tras hidratar`);
          if (valid.length >= 1) {
            setFeaturedProducts(valid.slice(0, 4));
            setFeaturedSource("ml-top");
            setLoadingProducts(false);
            return;
          }
        } else {
          console.log("[Home] No existe featuredProducts/topML aún. Usando modo aleatorio.");
        }
      } catch (err) {
        // si falla por permisos o lo que sea, caemos al modo aleatorio
        console.warn("No se pudo leer featuredProducts/topML:", err);
      }

      // 2) Fallback: muestreo aleatorio (comportamiento anterior)
      try {
        const productosRef = collection(db, "productos");
        // Solo necesitamos pocos productos destacados; pedimos un set acotado
        // y el random + filtro se hace client-side. Reduce drásticamente las
        // lecturas Firestore en cada visita al Home (mejor performance móvil).
        const q = query(
          productosRef,
          limit(40)
        );
        
        const snapshot = await getDocs(q);
        const productos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Filtrar productos de proveedores válidos, con stock y NO ocultos
        const productosDisponibles = productos.filter(p => 
          (p.provider === "onelectronics" || p.provider === "universotunning" || p.provider === "kits") &&
          p.stock > 0 &&
          p.hidden !== true
        );
        
        // Filtrar productos con imagen válida y precio razonable
        const productosConImagen = productosDisponibles.filter(p => 
          p.image && 
          p.image.startsWith('http') &&
          p.price > 1000
        );
        
        // Seleccionar 4 productos aleatorios
        const shuffled = productosConImagen.sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, 4);
        
        setFeaturedProducts(selected);
        setFeaturedSource("random");
        setProductsError(null);
      } catch (error) {
        console.error("Error cargando productos destacados:", error);
        setProductsError("No pudimos cargar los productos destacados.");
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  // Cargar kits / combos propios
  useEffect(() => {
    const fetchKits = async () => {
      try {
        const productosRef = collection(db, "productos");
        // Antes se pedian 80 productos cualquiera y se filtraba por "kits" en el
        // navegador: si ningun kit caia entre esos 80, la seccion quedaba vacia.
        const snapshot = await getDocs(query(productosRef, where("provider", "==", "kits"), limit(40)));
        const todos = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        const visibles = todos.filter(
          (p) =>
            p.provider === "kits" &&
            p.hidden !== true &&
            Number(p.stock || 0) > 0 &&
            p.image &&
            String(p.image).startsWith("http")
        );
        // Ordenar por fecha de creación descendente si existe
        visibles.sort((a, b) => {
          const ta = a.createdAt?.toDate?.()?.getTime?.() || 0;
          const tb = b.createdAt?.toDate?.()?.getTime?.() || 0;
          return tb - ta;
        });
        setKits(visibles.slice(0, 4));
      } catch (err) {
        console.error("Error cargando kits:", err);
      } finally {
        setLoadingKits(false);
      }
    };
    fetchKits();
  }, []);

  // Scroll al top cuando se carga la página
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const glowPulse = {
    animate: {
      boxShadow: [
        "0 0 20px rgba(0, 245, 255, 0.3)",
        "0 0 40px rgba(168, 85, 247, 0.4)",
        "0 0 20px rgba(0, 245, 255, 0.3)"
      ],
      transition: { duration: 2, repeat: Infinity }
    }
  };

  return (
    <main className="home-cyber">
      <Helmet>
        <title>Techteno ⚡ Tecnología y accesorios online en Argentina</title>
        <meta
          name="description"
          content="Tienda online de tecnología con envíos a todo el país. Mira los más vendidos en MercadoLibre, accesorios, audio, gaming, vehicular y más."
        />
        <meta property="og:title" content="Techteno ⚡ Tecnología que te acompaña" />
        <meta property="og:description" content="Productos de tecnología con precios actualizados y envío a todo el país." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://techteno.com/" />
      </Helmet>
      {/* HERO SECTION */}
      <section className="hero-section">
        <div className="hero-bg-effects">
          <div className="hero-orb hero-orb-1"></div>
          <div className="hero-orb hero-orb-2"></div>
          <div className="hero-orb hero-orb-3"></div>
          <div className="hero-grid"></div>
        </div>
        
        <motion.div 
          className="hero-content"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.span className="hero-badge" variants={fadeInUp}>
            <FaBolt /> TECNOLOGÍA DE ÚLTIMA GENERACIÓN
          </motion.span>
          
          <motion.h1 className="hero-title" variants={fadeInUp}>
            Tecnología que
            <span className="gradient-text-animated"> te acompaña </span>
          </motion.h1>
          
          <motion.p className="hero-subtitle" variants={fadeInUp}>
            Bienvenido a <strong>Techteno</strong> (también conocido como Tech Teno),
            tu tienda online de tecnología en Argentina. Descubrí productos exclusivos
            con la mejor relación calidad-precio: auriculares, smartwatches, gadgets y más.
          </motion.p>
          
          <motion.div className="hero-buttons" variants={fadeInUp}>
            <Link to="/productos#categorias">
              <motion.button 
                className="btn-primary-cyber"
                whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(0, 245, 255, 0.5)" }}
                whileTap={{ scale: 0.95 }}
              >
                <FaRocket /> Explorar Productos
              </motion.button>
            </Link>
            <Link to="/contacto">
              <motion.button 
                className="btn-secondary-cyber"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contáctanos
              </motion.button>
            </Link>
          </motion.div>

          <motion.div className="hero-stats" variants={fadeInUp}>
            <div className="stat-item">
              <span className="stat-number">500+</span>
              <span className="stat-label">Productos</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">5K+</span>
              <span className="stat-label">Clientes</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">5★</span>
              <span className="stat-label">Rating</span>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* CATEGORÍAS DESTACADAS — ahora arriba para acceso rápido desde el home */}
      <section className="categories-section categories-section-top" id="categorias-home">
        <motion.div
          className="section-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <span className="section-badge cat-badge">⚡ ACCESO RÁPIDO</span>
          <h2>Explorá por <span className="gradient-text">Categoría</span></h2>
          <p>Las más buscadas de la tienda</p>
        </motion.div>

        <motion.div
          className="categories-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          {[
            { name: "ACCESORIOS PC", label: "Accesorios PC", icon: <FaDesktop />, tone: "cyan", desc: "Teclados, mouse, hubs y más" },
            { name: "AURICULARES", label: "Auriculares", icon: <FaHeadphones />, tone: "cyan", desc: "Bluetooth, gamer e in‑ear" },
            { name: "CAMARAS DE SEGURIDAD", label: "Cámaras", icon: <FaVideo />, tone: "purple", desc: "WiFi, IP y vigilancia" },
            { name: "RELOJES", label: "Smartwatches", icon: <FaClock />, tone: "pink", desc: "Relojes inteligentes" },
            { name: "ACCESORIOS VEHICULOS", label: "Vehiculares", icon: <FaCar />, tone: "cyan", desc: "Auto, moto y bici" },
            { name: "CONVERTIDORES, CONSOLAS, TV", label: "Convertidores", icon: <FaTv />, tone: "purple", desc: "Smart TV y consolas" },
            { name: "ILUMINACION", label: "Iluminación", icon: <FaLightbulb />, tone: "pink", desc: "Luces LED y smart" },
            { name: "KITS", label: "Kits", icon: <FaGift />, tone: "cyan", desc: "Combos exclusivos" },
          ].map((cat) => (
            <motion.div key={cat.name} variants={fadeInUp} whileHover={{ y: -8, scale: 1.02 }}>
              <Link
                to={`/productos?category=${encodeURIComponent(cat.name)}`}
                className={`category-card tone-${cat.tone}`}
                aria-label={`Ver categoría ${cat.label}`}
              >
                <div className="category-icon">{cat.icon}</div>
                <div className="category-info">
                  <h3>{cat.label}</h3>
                  <p>{cat.desc}</p>
                </div>
                <span className="category-arrow" aria-hidden="true"><FaArrowRight /></span>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="categories-footer"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <Link to="/productos#categorias" className="categories-all-link">
            Ver todas las categorías <FaArrowRight />
          </Link>
        </motion.div>
      </section>

      {/* KITS / COMBOS SECTION */}
      <section className="kits-section" id="kits-home">
        <motion.div
          className="section-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <span className="section-badge kits-badge">🎁 COMBOS</span>
          <h2>Kits <span className="gradient-text">Techteno</span></h2>
          <p>Combos pensados para vos: más productos, mejor precio</p>
        </motion.div>

        <motion.div
          className="kits-grid"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {loadingKits ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="kit-card skeleton">
                <div className="skeleton-img"></div>
                <div className="skeleton-content">
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line short"></div>
                </div>
              </div>
            ))
          ) : kits.length === 0 ? (
            <div className="featured-error">
              <p>Estamos preparando nuevos combos. ¡Volvé pronto!</p>
              <Link to="/productos?category=KITS" className="btn-featured-error">
                Ver categoría Kits
              </Link>
            </div>
          ) : (
            <>
              {kits.map((kit, index) => (
                <motion.div
                  key={kit.id}
                  className="kit-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08, duration: 0.4 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                >
                  <div className="kit-badge">COMBO</div>
                  <div className="kit-img-wrap">
                    <img
                      src={kit.image ? getImageVariants(kit.image, "l").src : FALLBACK_IMAGE}
                      srcSet={kit.image ? getImageVariants(kit.image, "l").srcSet : undefined}
                      sizes="(max-width: 600px) 50vw, 300px"
                      alt={kit.name}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => imgFallback(e, kit.image)}
                    />
                  </div>
                  <div className="kit-content">
                    <h3>{kit.name?.length > 48 ? kit.name.substring(0, 48) + '...' : kit.name}</h3>
                    <div className="kit-pricing">
                      <span className="price-new">${Number(kit.price || 0).toLocaleString()}</span>
                    </div>
                    <Link to={`/productos/${kit.sourceId || kit.id}`}>
                      <motion.button
                        className="btn-kit"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FaGift /> Ver Combo
                      </motion.button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </>
          )}
        </motion.div>

        {kits.length > 0 && (
          <div className="kits-footer">
            <Link to="/productos?category=KITS" className="categories-all-link">
              Ver todos los kits <FaArrowRight />
            </Link>
          </div>
        )}
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="featured-section">
        <motion.div 
          className="section-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <span className="section-badge">
            {featuredSource === "ml-top" ? "🏆 BESTSELLERS" : "🔥 LO MÁS VENDIDO"}
          </span>
          <h2>
            {featuredSource === "ml-top" ? (
              <>Más vendidos en <span className="gradient-text">MercadoLibre</span></>
            ) : (
              <>Productos <span className="gradient-text">Destacados</span></>
            )}
          </h2>
          <p>
            {featuredSource === "ml-top"
              ? "Los favoritos de nuestros compradores en MercadoLibre"
              : "Elegidos especialmente para vos"}
          </p>
        </motion.div>

        <motion.div 
          className="featured-grid"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {loadingProducts ? (
            // Skeleton loaders
            [...Array(3)].map((_, i) => (
              <motion.div key={i} className="featured-card skeleton">
                <div className="skeleton-img"></div>
                <div className="skeleton-content">
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line short"></div>
                </div>
              </motion.div>
            ))
          ) : productsError ? (
            <div className="featured-error">
              <p>⚠️ {productsError}</p>
              <Link to="/productos" className="btn-featured-error">
                Ver catálogo completo
              </Link>
            </div>
          ) : featuredProducts.length === 0 ? (
            <div className="featured-error">
              <p>No hay productos destacados por ahora.</p>
              <Link to="/productos" className="btn-featured-error">
                Ver catálogo completo
              </Link>
            </div>
          ) : (
            <>
              {featuredProducts.slice(0, 3).map((product, index) => (
                <motion.div 
                  key={product.id}
                  className="featured-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                  whileHover={{ y: -15, scale: 1.02 }}
                >
                  {featuredSource === "ml-top" ? (
                    <>
                      {index === 0 && <div className="featured-badge">#1 ML</div>}
                      {index === 1 && <div className="featured-badge purple">#2 ML</div>}
                      {index === 2 && <div className="featured-badge pink">#3 ML</div>}
                    </>
                  ) : (
                    <>
                      {index === 0 && <div className="featured-badge">HOT</div>}
                      {index === 1 && <div className="featured-badge purple">TOP</div>}
                      {index === 2 && <div className="featured-badge pink">NEW</div>}
                    </>
                  )}
                  <div className="featured-img-wrap">
                    <img 
                      src={product.image ? getImageVariants(product.image, "l").src : FALLBACK_IMAGE}
                      srcSet={product.image ? getImageVariants(product.image, "l").srcSet : undefined}
                      sizes="(max-width: 600px) 50vw, 300px" 
                      alt={product.name} 
                      loading="lazy"
                      decoding="async"
                      onError={(e) => imgFallback(e, product.image)}
                    />
                  </div>
                  <div className="featured-content">
                    <span className="featured-category">{product.category}</span>
                    <h3>{product.name?.length > 40 ? product.name.substring(0, 40) + '...' : product.name}</h3>
                    <div className="featured-pricing">
                      <span className="price-new">${product.price?.toLocaleString()}</span>
                    </div>
                    <Link to={`/productos/${product.sourceId || product.id}`}>
                      <motion.button 
                        className="btn-featured"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FaShoppingCart /> Ver Producto
                      </motion.button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </>
          )}
        </motion.div>
      </section>

      {/* FEATURES SECTION */}
      <section className="features-section">
        <motion.div 
          className="features-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.div className="feature-card" variants={fadeInUp} whileHover={{ y: -10 }}>
            <div className="feature-icon-wrap">
              <FaTruck />
            </div>
            <h3>Envío Rápido</h3>
            <p>Entregas en 24-72hs a todo el país</p>
          </motion.div>

          <motion.div className="feature-card" variants={fadeInUp} whileHover={{ y: -10 }}>
            <div className="feature-icon-wrap purple">
              <FaShieldAlt />
            </div>
            <h3>Compra Segura</h3>
            <p>Transacciones 100% protegidas</p>
          </motion.div>

          <motion.div className="feature-card" variants={fadeInUp} whileHover={{ y: -10 }}>
            <div className="feature-icon-wrap pink">
              <FaHeadset />
            </div>
            <h3>Soporte 24/7</h3>
            <p>Atención personalizada siempre</p>
          </motion.div>
        </motion.div>
      </section>

      {/* CTA SECTION */}
      <section className="cta-section">
        <motion.div 
          className="cta-container"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          {...glowPulse}
        >
          <div className="cta-bg-pattern"></div>
          <h2>¿Listo para el Futuro?</h2>
          <p>Explorá cientos de productos tecnológicos con las mejores ofertas del mercado.</p>
          <Link to="/productos#categorias">
            <motion.button 
              className="btn-cta-main"
              whileHover={{ scale: 1.08, boxShadow: "0 0 40px rgba(0, 245, 255, 0.6)" }}
              whileTap={{ scale: 0.95 }}
            >
              <FaRocket /> Comenzar Ahora
            </motion.button>
          </Link>
        </motion.div>
      </section>
    </main>
  );
};

export default Home;
